import { assertNovelUrlAllowed, renderNovelPage } from './renderNovelPage';
import { bundleAssets, MAX_TOTAL_ASSET_BYTES } from '@/services/send/conversion/assetBundler';
import { Readability } from '@mozilla/readability';
import { sanitizeHtml } from '@/utils/sanitize';
import { detectLanguage } from '@/utils/lang';
import { stubTranslation as _ } from '@/utils/misc';
import { buildEpub } from '@/services/send/conversion/buildEpub';
import { generateCoverSvg } from '@/services/send/conversion/coverGenerator';
import { fetchAuthorImage } from '@/services/send/conversion/faviconFetcher';
import {
  parsePageDocument,
  safeFileName,
  stableIdentifier,
  stripTags,
} from '@/services/send/conversion/convertToEpub';
import {
  isLikelyBotBlock,
  isTransientUpstreamError,
  pageNavigateHeaders,
} from '@/services/send/conversion/httpHeaders';
import { ConversionError } from '@/services/send/conversion/types';
import type { ConvertedBook, EpubChapter, EpubImage } from '@/services/send/conversion/types';
import { parseChapterList, parseWorkMetadata } from './chapterList';
import type { NovelChapterLink, NovelToc, WorkMetadata } from './chapterList';

/** Hard cap — a runaway TOC heuristic must not schedule tens of thousands of
 *  requests. Real novels top out around this size. */
export const MAX_NOVEL_CHAPTERS = 2000;

const PAGE_FETCH_TIMEOUT_MS = 15_000;
const CHAPTER_CONCURRENCY = 4;
/** Below this many characters the "chapter" is a JS-challenge stub or an
 *  extraction miss, not prose. */
const CHAPTER_QUALITY_FLOOR = 100;

export interface FetchedPage {
  html: string;
  finalUrl: string;
}

export type FetchPage = (url: string, signal?: AbortSignal) => Promise<FetchedPage>;
export type FetchCover = (
  url: string,
  referer: string,
) => Promise<{ bytes: ArrayBuffer; mime: string } | null>;

export interface NovelBook extends ConvertedBook {
  chapterCount: number;
  failures: number;
}

export interface NovelDownloadOptions {
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
  /** Stable input for the EPUB identifier. Defaults to the chapter-list URL;
   *  selected volumes supply their chapter URLs so each volume stays distinct. */
  identityKey?: string;
  fetchPage?: FetchPage;
  fetchCover?: FetchCover;
  renderPage?: FetchPage;
  /** Sign-in imports render every chapter, including sites with long public previews. */
  renderChapters?: boolean;
  /** Runtime translator for text baked into the EPUB (failure placeholders).
   *  Strings are declared with `stubTranslation` so the i18n scanner sees
   *  them; pass the app's `_` to localize. */
  translate?: (key: string) => string;
}

const abortError = () => new DOMException('novel import cancelled', 'AbortError');

export function isNovelImportCancelled(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

const isTransientFetchError = (err: unknown): boolean =>
  err instanceof ConversionError && err.code === 'fetch_transient';

/** Extra attempts for an origin that is briefly unreachable, and the base
 *  delay between them (linear backoff: 1s, then 2s). */
const TRANSIENT_RETRIES = 2;
const TRANSIENT_BACKOFF_MS = 1000;

/**
 * Wrap a fetcher with the per-request deadline and a backoff retry, so a
 * briefly-unreachable origin doesn't fail the import outright. A site fronted
 * by Cloudflare can 52x — or simply hang — on most requests for minutes at a
 * time while staying perfectly readable in between, which otherwise leaves the
 * user hammering the button by hand.
 */
const withTransientRetry =
  (fetchPage: FetchPage): FetchPage =>
  async (url, signal) => {
    assertNovelUrlAllowed(url);
    for (let attempt = 0; ; attempt++) {
      const deadline = new AbortController();
      const onAbort = () => deadline.abort();
      signal?.addEventListener('abort', onAbort, { once: true });
      let timer: ReturnType<typeof setTimeout> | undefined;
      let failure: unknown;
      try {
        const page = fetchPage(url, deadline.signal);
        // Race rather than trusting the fetcher to observe the abort, so a
        // hung origin can never wedge the import. The deadline expiring is a
        // retryable failure, not the user cancelling.
        page.catch(() => {});
        return await Promise.race([
          page,
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => {
              deadline.abort();
              reject(
                new ConversionError(
                  'The site took too long to respond. Try again in a moment.',
                  'fetch_transient',
                ),
              );
            }, PAGE_FETCH_TIMEOUT_MS);
          }),
        ]);
      } catch (err) {
        failure = err;
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
      }
      if (attempt >= TRANSIENT_RETRIES || !isTransientFetchError(failure)) throw failure;
      if (signal?.aborted) throw abortError();
      await new Promise((resolve) => setTimeout(resolve, TRANSIENT_BACKOFF_MS * (attempt + 1)));
      if (signal?.aborted) throw abortError();
    }
  };

/**
 * The number of leading bytes scanned for a `<meta>` charset declaration.
 * Matches the prescan window browsers use for the same purpose.
 */
const CHARSET_SNIFF_BYTES = 1024;

/** `charset=` out of a `Content-Type` value, quoted or bare. */
const charsetOf = (contentType: string | null | undefined): string | null =>
  contentType?.match(/charset\s*=\s*['"]?([^'";,\s]+)/i)?.[1] ?? null;

const decoderFor = (label: string | null): TextDecoder | null => {
  if (!label) return null;
  try {
    return new TextDecoder(label, { ignoreBOM: false });
  } catch {
    // An unregistered label ("x-nonsense", a typo) — treat it as undeclared.
    return null;
  }
};

/**
 * `<meta charset=…>` or `<meta http-equiv=content-type content="…charset=…">`
 * from the head of the document. The window is parsed rather than pattern
 * matched, so a declaration sitting in a comment or a script string cannot be
 * mistaken for a real one and the parser handles attribute quoting for us.
 * Decoding it as windows-1252 first is safe: every encoding this matters for
 * is ASCII-compatible in its markup.
 */
const sniffMetaCharset = (bytes: Uint8Array): string | null => {
  const head = new TextDecoder('windows-1252').decode(bytes.subarray(0, CHARSET_SNIFF_BYTES));
  const doc = new DOMParser().parseFromString(head, 'text/html');
  for (const meta of doc.querySelectorAll('meta')) {
    const declared = /^content-type$/i.test(meta.getAttribute('http-equiv') || '')
      ? charsetOf(meta.getAttribute('content'))
      : meta.getAttribute('charset');
    const charset = declared?.trim();
    if (charset) return charset;
  }
  return null;
};

/**
 * Decode a fetched page body the way a browser would.
 *
 * `Response.text()` always decodes as UTF-8 regardless of what the page says,
 * so it turns a legacy-encoded site into mojibake — and Chinese web-novel
 * sites overwhelmingly still serve GB2312/GBK, usually declaring it only in a
 * `<meta>` tag because the HTTP `Content-Type` carries no charset. Follow the
 * sniffing order that gets those pages right: BOM, then the response header,
 * then the `<meta>` prescan.
 */
export function decodeHtmlBody(bytes: Uint8Array, contentType: string | null): string {
  // A BOM outranks every declaration, and `TextDecoder` strips it for us.
  const [b0, b1, b2] = bytes;
  if (b0 === 0xff && b1 === 0xfe) return new TextDecoder('utf-16le').decode(bytes);
  if (b0 === 0xfe && b1 === 0xff) return new TextDecoder('utf-16be').decode(bytes);
  if (b0 === 0xef && b1 === 0xbb && b2 === 0xbf) return new TextDecoder('utf-8').decode(bytes);

  const declared = decoderFor(charsetOf(contentType)) ?? decoderFor(sniffMetaCharset(bytes));
  if (declared) return declared.decode(bytes);

  // Nothing declared. UTF-8 when the bytes actually are UTF-8; otherwise
  // GB18030, which decodes every byte sequence and is what an undeclared
  // legacy page on this part of the web almost always turns out to be.
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('gb18030').decode(bytes);
  }
}

/**
 * Fetch server-rendered chapters with the browser session and browser-shaped
 * headers. JavaScript-only chapters fall back to the native renderer; imports
 * started through sign-in render every chapter so public previews cannot win.
 */
const defaultFetchPage: FetchPage = async (url, signal) => {
  const { browserFetch: tauriFetch } = await import('@/services/webBrowser/browserFetch');
  const res = await tauriFetch(url, {
    headers: pageNavigateHeaders(),
    signal,
    redirect: 'follow',
  });
  if (!res.ok) {
    if (isTransientUpstreamError(res.status)) {
      throw new ConversionError(
        `The site is temporarily unavailable (HTTP ${res.status}). Try again in a moment.`,
        'fetch_transient',
      );
    }
    throw new ConversionError(
      isLikelyBotBlock(res.status)
        ? `The site blocked the request (HTTP ${res.status}). Try again later.`
        : `Could not fetch the page (HTTP ${res.status}).`,
      'fetch_failed',
    );
  }
  const body = new Uint8Array(await res.arrayBuffer());
  return {
    html: decodeHtmlBody(body, res.headers.get('content-type')),
    finalUrl: res.url || url,
  };
};

const defaultFetchCover: FetchCover = (url, referer) =>
  fetchAuthorImage(url, referer).catch(() => null);

export interface FetchNovelTocOptions {
  /** The chapter list captured after the user signs in and navigates. */
  page?: FetchedPage;
  fetchPage?: FetchPage;
  signal?: AbortSignal;
}

/** Fetch a TOC page and parse it into a capped, ordered chapter list. */
export async function fetchNovelToc(
  url: string,
  options: FetchNovelTocOptions = {},
): Promise<NovelToc> {
  const fetchPage = withTransientRetry(options.fetchPage ?? defaultFetchPage);
  const { html, finalUrl } = options.page ?? (await fetchPage(url, options.signal));
  assertNovelUrlAllowed(finalUrl);
  const toc = parseChapterList(html, finalUrl);
  if (!toc) {
    throw new ConversionError(
      'No chapter list found on this page. Open the novel’s table-of-contents page and try again.',
      'parse_failed',
    );
  }
  const chapters = toc.chapters.slice(0, MAX_NOVEL_CHAPTERS);
  return {
    ...toc,
    ...(await backfillMetadata(toc, chapters, fetchPage, options.signal)),
    chapters,
  };
}

/**
 * A chapter-index page is often just a list of links, with the work's real
 * title and author only on the chapter pages. When the index could offer no
 * more than a page-level guess, ask the first chapter instead. Best-effort:
 * the guess stands if that fetch or parse comes up empty.
 */
async function backfillMetadata(
  toc: NovelToc,
  chapters: NovelChapterLink[],
  fetchPage: FetchPage,
  signal?: AbortSignal,
): Promise<Partial<NovelToc>> {
  const first = chapters[0];
  if (!first || (!toc.weak.title && !toc.weak.author)) return {};
  let found: WorkMetadata;
  try {
    const { html } = await fetchPage(first.url, signal);
    found = parseWorkMetadata(html);
  } catch (err) {
    if (isNovelImportCancelled(err) || signal?.aborted) throw abortError();
    return {};
  }
  return {
    ...(toc.weak.title && found.title ? { title: found.title } : {}),
    ...(toc.weak.author && found.author ? { author: found.author } : {}),
  };
}

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Selectors novel sites use for the chapter body when Readability mis-scores
// the page (bare `<div id="content">` full of `<br>`-separated text lines is
// common and scores poorly).
const CHAPTER_CONTENT_SELECTORS = [
  '#chaptercontent',
  '#chapter-content',
  '.chapter-content',
  '#booktxt',
  '#content',
  '.read-content',
  'article',
  '.content',
];

const normalizeTitle = (s: string): string => s.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Extract one chapter's prose. Readability first, known content selectors as
 * fallback; assets are bundled separately and the TOC's chapter title is prepended as the
 * canonical heading.
 */
export function extractChapterHtml(
  pageHtml: string,
  chapterTitle: string,
  pageUrl: string,
): string | null {
  let content: string | null = null;
  try {
    // Readability mutates the document it scores, so give it its own parse.
    const doc = parsePageDocument(pageHtml, pageUrl);
    content = new Readability(doc).parse()?.content ?? null;
  } catch {
    content = null;
  }
  if (!content || stripTags(content).length < CHAPTER_QUALITY_FLOOR) {
    const doc = parsePageDocument(pageHtml, pageUrl);
    for (const selector of CHAPTER_CONTENT_SELECTORS) {
      const el = doc.querySelector(selector);
      if (el && stripTags(el.innerHTML).length >= CHAPTER_QUALITY_FLOOR) {
        content = el.innerHTML;
        break;
      }
    }
  }
  if (!content || stripTags(content).length < CHAPTER_QUALITY_FLOOR) return null;

  const body = new DOMParser().parseFromString(content, 'text/html').body;
  body.querySelectorAll('video, audio').forEach((el) => el.remove());
  // Drop a leading heading that duplicates the TOC title — the canonical
  // heading is prepended below.
  const heading = body.querySelector('h1, h2, h3');
  if (heading && normalizeTitle(heading.textContent || '') === normalizeTitle(chapterTitle)) {
    heading.remove();
  }
  return `<h1>${escapeHtml(chapterTitle)}</h1>\n${sanitizeHtml(body.innerHTML)}`;
}

/**
 * Download every chapter of a parsed novel TOC and assemble a single EPUB.
 * Individual chapter failures become placeholder pages (with the source URL)
 * rather than aborting a long download; `failures` reports the count.
 */
export async function downloadNovel(
  toc: NovelToc,
  sourceUrl: string,
  options: NovelDownloadOptions = {},
): Promise<NovelBook> {
  const fetchPage = withTransientRetry(options.fetchPage ?? defaultFetchPage);
  const fetchCover = options.fetchCover ?? defaultFetchCover;
  const translate = options.translate ?? ((key: string) => key);
  const { onProgress, identityKey = sourceUrl } = options;
  const controller = new AbortController();
  const signal = controller.signal;
  const onAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onAbort, { once: true });
  if (options.signal?.aborted) controller.abort();
  try {
    signal.throwIfAborted();

    const chapters = toc.chapters.slice(0, MAX_NOVEL_CHAPTERS);
    const total = chapters.length;
    const results: EpubChapter[] = new Array(total);
    const images: EpubImage[][] = new Array(total);
    let remainingAssetBytes = MAX_TOTAL_ASSET_BYTES;
    let assetQueue: Promise<unknown> = Promise.resolve();
    const renderPage =
      options.renderPage ??
      (!options.fetchPage
        ? (url: string, signal?: AbortSignal) => renderNovelPage(url, signal, translate)
        : undefined);
    let failures = 0;
    let done = 0;
    let cursor = 0;

    const fetchChapter = async (url: string): Promise<FetchedPage> => {
      try {
        return await fetchPage(url, signal);
      } catch (err) {
        // One retry on transient network errors; user cancellation propagates.
        if (isNovelImportCancelled(err) || signal.aborted) {
          controller.abort();
          throw abortError();
        }
        // A 52x has already been retried with backoff — don't double up.
        if (isTransientFetchError(err)) throw err;
        return await fetchPage(url, signal);
      }
    };

    const worker = async () => {
      while (true) {
        if (signal?.aborted) throw abortError();
        const index = cursor++;
        if (index >= total) return;
        const link = chapters[index]!;
        let html: string | null = null;
        try {
          assertNovelUrlAllowed(link.url);
          let page: FetchedPage;
          let rendered = false;
          try {
            if (options.renderChapters && renderPage) {
              page = await renderPage(link.url, signal);
              rendered = true;
            } else {
              page = await fetchChapter(link.url);
            }
          } catch (err) {
            if (
              signal?.aborted ||
              isNovelImportCancelled(err) ||
              !renderPage ||
              options.renderChapters ||
              isTransientFetchError(err)
            )
              throw err;
            page = await renderPage(link.url, signal);
            rendered = true;
          }
          html = extractChapterHtml(page.html, link.title, page.finalUrl);
          if (html === null && renderPage && !rendered) {
            assertNovelUrlAllowed(page.finalUrl);
            page = await renderPage(page.finalUrl, signal);
            html = extractChapterHtml(page.html, link.title, page.finalUrl);
          }
          if (html !== null) {
            const content = html;
            const bundled = assetQueue.then(async () => {
              if (signal?.aborted) throw abortError();
              const bundle = await bundleAssets(content, page.finalUrl, {
                maxBytes: remainingAssetBytes,
                signal,
              });
              remainingAssetBytes -= bundle.images.reduce(
                (sum, image) => sum + image.bytes.byteLength,
                0,
              );
              return bundle;
            });
            assetQueue = bundled.catch(() => {});
            const bundle = await bundled;
            html = bundle.html;
            images[index] = bundle.images;
          }
        } catch (err) {
          if (isNovelImportCancelled(err) || signal.aborted) {
            controller.abort();
            throw abortError();
          }
          html = null;
        }
        if (html === null) {
          failures++;
          html =
            `<h1>${escapeHtml(link.title)}</h1>` +
            `<p>${escapeHtml(translate(_('This chapter could not be downloaded.')))}</p>` +
            `<p><a href="${escapeHtml(link.url)}">${escapeHtml(link.url)}</a></p>`;
        }
        results[index] = { title: link.title, html };
        done++;
        onProgress?.(done, total);
        // Extraction is main-thread CPU work (Tauri IPC can't run in a Worker) —
        // yield between chapters so a long download doesn't freeze the UI.
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    };
    await Promise.all(Array.from({ length: Math.min(CHAPTER_CONCURRENCY, total) }, worker));

    let cover: EpubImage | undefined;
    if (toc.coverUrl) {
      const fetched = await fetchCover(toc.coverUrl, sourceUrl);
      if (fetched) {
        const ext = fetched.mime.split('/')[1]?.replace('jpeg', 'jpg') || 'jpg';
        cover = { path: `cover.${ext}`, bytes: fetched.bytes, mime: fetched.mime };
      }
    }
    if (!cover) {
      const siteName = (() => {
        try {
          return new URL(sourceUrl).hostname.replace(/^www\./, '');
        } catch {
          return '';
        }
      })();
      cover = generateCoverSvg({ title: toc.title, author: toc.author, siteName });
    }

    // Sample prose (not placeholder text) for language detection.
    let sample = '';
    for (const chapter of results) {
      sample += ` ${stripTags(chapter.html)}`;
      if (sample.length >= 2048) break;
    }
    const language = detectLanguage(`${toc.title} ${sample}`.slice(0, 2048)) || 'en';

    signal.throwIfAborted();
    const blob = await buildEpub(
      results,
      {
        title: toc.title,
        author: toc.author,
        language,
        identifier: stableIdentifier(identityKey),
      },
      [
        ...new Map(
          images
            .flat()
            .filter(Boolean)
            .map((image) => [image.path, image]),
        ).values(),
      ],
      cover,
    );
    const file = new File([blob], `${safeFileName(toc.title)}.epub`, {
      type: 'application/epub+zip',
    });
    signal.throwIfAborted();
    return { file, title: toc.title, author: toc.author, chapterCount: total, failures };
  } finally {
    options.signal?.removeEventListener('abort', onAbort);
  }
}
